'use client';

import { Box, Divider, Stack } from '@unity/cloud-ui';
import { z } from 'zod';
import { useTranslations } from 'next-intl';
import { getComponentDocumentationUrl } from '@/utils/get-component-doc-url';
import { Header } from '../header';
import { Code } from '../code';

export function FieldGroup(props: z.input<typeof fieldGroupSchema>) {
  return (
    <Box>
      <Stack direction="column" spacing={2} divider={<Divider />}>
        {props.children}
      </Stack>
    </Box>
  );
}

export function Field(p: z.input<typeof fieldSchema>) {
  const props = fieldSchema.parse(p);
  const t = useTranslations();

  return (
    <Box>
      <Stack direction="column" spacing={2}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Box>
              <Header
                variant="h4"
                sx={{
                  fontFamily: 'Consolas, monospace',
                }}
              >
                <Box
                  component="span"
                  sx={{
                    color: ({ vars }) => vars.palette.primary.main,
                    fontWeight: ({ typography }) => typography.fontWeightBold,
                  }}
                >
                  {props.name || props.title}
                </Box>
              </Header>
            </Box>
            {props.type && (
              <Box>
                <Code>{props.type}</Code>
              </Box>
            )}
            {props.default && (
              <Box>
                <Code>{`${t('mdx:field:default')}: ${props.default}`}</Code>
              </Box>
            )}
            {props.required && (
              <Box>
                <Code
                  sx={{
                    color: ({ vars }) => vars.palette.error.dark,
                    backgroundColor: ({ vars }) => vars.palette.error.lighter,
                  }}
                >
                  {t('mdx:field:required')}
                </Code>
              </Box>
            )}
          </Stack>
          {props.example && (
            <Box>
              <Code>{`${t('mdx:field:example')}: ${props.example}`}</Code>
            </Box>
          )}
        </Box>
        {props.children}
      </Stack>
    </Box>
  );
}

const documentationUrl = getComponentDocumentationUrl('fields');

const descriptions = {
  field: {
    schema: `Help define the parameters for your APIs or SDKs. \n\nCan be a child of \`FieldGroup\`. \n\n**Example**\n\`\`\`mdx\n<Field name="name" type="string" required>\n  The name of the user\n</Field>\n\`\`\`\n\n${documentationUrl}`,
    title: 'The name of the field. Must be either name or title.',
    name: 'Deprecated. Use `title` instead.',
    type: 'The type of the field. ex: string, number',
    default: 'The default value of the field.',
    example: 'An example value for the field.',
    required: 'Whether the field is required.',
    highlight: 'Whether or not to highlight the field',
    children: 'The text content of the field. Explain the Field here.',
  },
  fieldGroup: {
    schema: `Help group your fields together. \n\nCan be a child of \`FieldGroup\` or \`Field\`. \n\n**Example**\n\`\`\`mdx\n<FieldGroup title="User Information">\n  <Field name="name" type="string" required>\n    The name of the user\n  </Field>\n</FieldGroup>\n\`\`\`\n\n${documentationUrl}`,
    children: 'The content of the field group',
  },
};

export const fieldSchema = z
  .object({
    name: z.string().describe(descriptions.field.name).optional(),
    title: z.string().describe(descriptions.field.title).optional(),
    type: z.string().describe(descriptions.field.type).optional(),
    default: z.string().describe(descriptions.field.default).optional(),
    example: z.string().describe(descriptions.field.example).optional(),
    required: z.boolean().describe(descriptions.field.required).optional(),
    highlight: z.boolean().describe(descriptions.field.highlight).optional(),
    children: z.any().describe(descriptions.field.children).optional(),
  })
  .describe(descriptions.field.schema)
  .refine(
    (data) => {
      return (
        (data.title !== undefined && data.name === undefined) || (data.title === undefined && data.name !== undefined)
      );
    },
    {
      message: `Field must contain either 'title' or 'name', but not both.`,
    },
  );

export const fieldGroupSchema = z
  .object({
    children: z.any().describe(descriptions.fieldGroup.children),
  })
  .describe(descriptions.fieldGroup.schema);
